import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Receipt, RefreshCw, Send, AlertOctagon } from "lucide-react";
import Layout from "../components/Layout";
import AiOutputDisclaimer from "../components/AiOutputDisclaimer";
import { api } from "../lib/api";
import { errMsg } from "../lib/errors";

const STATUS_COLOR = {
  submitted: "text-blue-300 bg-blue-500/15 border-blue-500/40",
  pending: "text-amber-300 bg-amber-500/15 border-amber-500/40",
  paid: "text-emerald-300 bg-emerald-500/15 border-emerald-500/40",
  denied: "text-red-300 bg-red-500/15 border-red-500/40",
  appeal_drafted: "text-purple-300 bg-purple-500/15 border-purple-500/40",
};

export default function ReimburseProClaims() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [payor, setPayor] = useState("");
  const [memberId, setMemberId] = useState("");
  const [busy, setBusy] = useState(false);
  const [appealing, setAppealing] = useState(null);
  const [denialReason, setDenialReason] = useState("");
  const [appeal, setAppeal] = useState(null);

  const load = async () => {
    const r = await api.get("/reimbursepro/claims");
    setClaims(r.data || []);
    setLoading(false);
  };
  useEffect(() => {
    load();
    api.get("/treatment-plan").then((r) => setPlans(r.data || []));
  }, []);

  const pushClaim = async () => {
    if (!planId || !payor || !memberId) { toast.error("Plan, payor, and member ID required"); return; }
    setBusy(true);
    try {
      await api.post(`/reimbursepro/push-claim/${planId}`, { payor, member_id: memberId, submit_now: true });
      toast.success("Claim pushed to ReimbursePro");
      setPlanId(""); setPayor(""); setMemberId("");
      load();
    } catch (e) { toast.error(errMsg(e, "Push failed")); }
    finally { setBusy(false); }
  };

  const syncClaim = async (planId) => {
    try {
      await api.get(`/reimbursepro/claim/${planId}`);
      toast.success("Synced");
      load();
    } catch (e) { toast.error(errMsg(e, "Sync failed")); }
  };

  const drafttAppeal = async (claim) => {
    if (!denialReason.trim()) { toast.error("Enter denial reason"); return; }
    setAppealing(claim.claim_id);
    try {
      const r = await api.post(`/reimbursepro/appeal/${claim.claim_id}`, { denial_reason: denialReason });
      setAppeal(r.data.appeal);
      toast.success("Appeal drafted");
      load();
    } catch (e) { toast.error(errMsg(e, "Appeal generation failed")); }
    finally { setAppealing(null); }
  };

  const iCl = "w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-2";

  return (
    <Layout>
      <div className="kicker">Phase 5 · ReimbursePro</div>
      <h1 data-testid="rp-title" className="font-serif text-3xl text-[var(--pa-text)] mt-1 flex items-center gap-3">
        <Receipt size={22} className="text-[var(--pa-gold)]" /> Claims &amp; Billing
      </h1>
      <p className="mt-2 text-sm text-[var(--pa-text-muted)]">Bidirectional sync with medical-reimburse.emergent.host — clinical-to-billing pipeline + claim status + denial-to-appeal.</p>

      <section className="mt-6 bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-5 max-w-3xl" data-testid="rp-push-form">
        <div className="kicker mb-3">Push a treatment plan as a claim</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <select value={planId} onChange={(e) => setPlanId(e.target.value)} className={iCl} data-testid="rp-plan">
            <option value="">— Plan —</option>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.patient_name} ({new Date(p.created_at).toLocaleDateString()})</option>)}
          </select>
          <input placeholder="Payor (e.g. Aetna)" value={payor} onChange={(e) => setPayor(e.target.value)} className={iCl} data-testid="rp-payor" />
          <input placeholder="Member ID" value={memberId} onChange={(e) => setMemberId(e.target.value)} className={iCl} data-testid="rp-member" />
        </div>
        <button onClick={pushClaim} disabled={busy} data-testid="rp-submit"
          className="mt-3 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-5 py-2 rounded flex items-center gap-2 text-sm disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Submit claim
        </button>
      </section>

      <section className="mt-8">
        <div className="kicker mb-3">All claims</div>
        {loading ? <Loader2 className="animate-spin text-[var(--pa-gold)]" size={16} /> : claims.length === 0 ?
          <div className="text-sm text-[var(--pa-text-dim)]">No claims yet.</div> : (
          <div className="space-y-2" data-testid="rp-claims">
            {claims.map((c) => (
              <div key={c.id} className="border border-[var(--pa-border)] bg-[var(--pa-bg-2)] rounded p-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono font-semibold text-[var(--pa-gold)]">{c.claim_id}</span>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_COLOR[c.status] || "border-[var(--pa-border)] text-[var(--pa-text-muted)]"}`}>{c.status}</span>
                  <span className="text-sm text-[var(--pa-text)]">{c.patient_name}</span>
                  <span className="text-xs text-[var(--pa-text-dim)]">{c.payor} · {c.procedures_count} procs · ${(c.total_fee_usd || 0).toLocaleString()}</span>
                  <button onClick={() => syncClaim(c.treatment_plan_id)} className="ml-auto text-xs text-[var(--pa-gold)] hover:underline flex items-center gap-1" data-testid={`rp-sync-${c.claim_id}`}>
                    <RefreshCw size={11} /> Sync
                  </button>
                </div>
                {c.history?.length > 0 && (
                  <div className="mt-2 flex gap-1.5 flex-wrap text-[10px]">
                    {c.history.map((h, i) => (
                      <span key={i} className="px-1.5 py-0.5 rounded bg-[var(--pa-bg)] border border-[var(--pa-border)] text-[var(--pa-text-dim)]">
                        {h.status} ({h.via})
                      </span>
                    ))}
                  </div>
                )}
                {c.status === "denied" && (
                  <div className="mt-3 border-t border-[var(--pa-border)] pt-3">
                    <div className="kicker text-red-300 mb-1">Generate appeal</div>
                    <textarea value={denialReason} onChange={(e) => setDenialReason(e.target.value)} rows={2}
                      placeholder="Payor denial reason (paste from EOB)" data-testid={`rp-denial-${c.claim_id}`}
                      className={`${iCl} w-full text-xs`} />
                    <button onClick={() => drafttAppeal(c)} disabled={appealing === c.claim_id}
                      data-testid={`rp-appeal-${c.claim_id}`}
                      className="mt-2 text-xs bg-red-500/20 border border-red-500/50 text-red-300 px-3 py-1.5 rounded flex items-center gap-1 disabled:opacity-60">
                      {appealing === c.claim_id ? <Loader2 size={12} className="animate-spin" /> : <AlertOctagon size={12} />} Draft appeal letter
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {appeal && (
        <section className="mt-8 border border-purple-500/30 bg-purple-500/5 rounded p-5" data-testid="rp-appeal-result">
          <div className="kicker text-purple-300">Appeal letter draft</div>
          <div className="text-xs text-[var(--pa-text-muted)] mt-1">Estimated success probability: <span className="text-purple-300 font-semibold">{appeal.estimated_success_probability_pct}%</span></div>
          <AiOutputDisclaimer className="mt-3" />
          <pre className="mt-3 text-xs whitespace-pre-wrap bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-3 text-[var(--pa-text)] font-sans leading-relaxed">{appeal.appeal_letter_markdown}</pre>
        </section>
      )}
    </Layout>
  );
}
